import { PREDICT_LABEL_TEXT, trendResolutionDetail } from "../lib/barrierCopy";
import { resolveRound } from "../lib/resolve";
import type { RoundSource, Timeframe } from "../lib/rounds";
import type { BinaryLabel, PresetResolution, Round } from "../types";
import type { HistoryEntryInput } from "./historyStore";
import { createRoundLoop, type FetchLike } from "./roundLoop";
import { trendRoundFilter, useSettingsStore } from "./settingsStore";

/**
 * The Predict trend Loop, over any timeframe's bank (ADR-0013).
 *
 * Every bank stores a Forward path now, so the trend game is one adapter
 * parameterised by timeframe rather than a daily-only store: the barriers, the
 * preset and the reveal are all in candles and ATR, which read the same
 * whatever a candle spans. Only the bank, the persistence keys and the
 * timeframe stamped on a history entry differ between instances.
 */

/** What a trend Loop reveals: the resolution it was scored against. */
export type TrendReveal = {
  resolution: PresetResolution;
};

/** Resolve a trend commitment against the active difficulty preset. */
export function resolveTrend(
  round: Round,
  guess: BinaryLabel,
): { reveal: TrendReveal; correct: boolean } {
  const { preset } = useSettingsStore.getState();
  const resolution = resolveRound(round, preset);
  return { reveal: { resolution }, correct: guess === resolution.label };
}

/** A trend Loop's history entry: the Higher/Lower guess against the resolved label. */
function historyTrend(timeframe: Timeframe) {
  return (
    round: Round,
    guess: BinaryLabel,
    reveal: TrendReveal,
    correct: boolean | undefined,
    note: string | undefined,
  ): HistoryEntryInput => {
    const { contextLength, preset } = useSettingsStore.getState();
    return {
      mode: "predict",
      timeframe,
      horizon: "trend",
      kind: "free",
      roundId: round.id,
      preset,
      contextLength,
      instrumentId: round.instrumentId,
      instrumentName: round.instrumentName,
      dateRange: round.dateRange,
      choice: PREDICT_LABEL_TEXT[guess],
      outcome: correct ? "correct" : "incorrect",
      detail: trendResolutionDetail(reveal.resolution),
      note,
    };
  };
}

export type TrendLoopConfig = {
  /** Which timeframe this instance plays: its bank, and its history stamp. */
  timeframe: Timeframe;
  /** The bank's shared source (see `TIMEFRAME_SOURCE` in lib/rounds.ts). */
  openSource: (baseUrl: string, fetchFn: FetchLike) => Promise<RoundSource<Round>>;
  /** Storage key for this Loop's seen-Round ids. */
  seenKey: string;
  scoreKey: string;
  streakKey: string;
};

/** Build one timeframe's Predict trend Loop. */
export function createTrendLoop(config: TrendLoopConfig) {
  return createRoundLoop({
    openSource: config.openSource,
    seenKey: config.seenKey,
    filter: () => trendRoundFilter(config.timeframe),
    resolve: resolveTrend,
    scoring: { scoreKey: config.scoreKey, streakKey: config.streakKey },
    history: historyTrend(config.timeframe),
    analytics: {
      mode: "predict",
      timeframe: config.timeframe,
      horizon: "trend",
      preset: () => useSettingsStore.getState().preset,
    },
  });
}
